import { Alert, Autocomplete, Button, Checkbox, FormControl, FormControlLabel, FormGroup, Grid, TextField } from '@mui/material';
import { ChangeEvent, FormEvent, SyntheticEvent, useContext, useEffect, useState } from 'react';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import { StockCountSummaryReportRequest, Store } from '../../../../app/services/api/generated';
import { useTranslation } from 'react-i18next';
import { formatDateForReport } from '../../../../utils';
import { UserContext } from '../../../../components/shared/useUser';

type IProps = {
  onClose?: () => void;
}

const StockCountSummaryReportForm = (props: IProps) => {
  const { user, selectedFranchise, isAdmin } = useContext(UserContext);
  const [selectedStores, setSelectedStores] = useState<Store[]>([]);
  const [date, setDate] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isRequestSubmitted, setIsRequestSubmitted] = useState<boolean>(false);
  const [checkedAllStores, setCheckedAllStores] = useState<boolean>(false);
  const { runStockCountSummaryReport } = useNswagClient();
  const { t } = useTranslation('common');

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setSelectedStores([]);
    setCheckedAllStores(checked);
  };

  const getMaxDate = () => {
    const today = new Date().toISOString().split('T')[0];
    return today;
  };

  const getMinDate = () => {
    const minDate = new Date('1900-01-01').toISOString().split('T')[0];
    return minDate;
  };

  const handleDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDate(event.target.value);
  };

  const handleSelectChange = (event: SyntheticEvent, value: Store[]) => {
    setSelectedStores(value);
  };

  const isMultipleFranchiseUser = (user?.franchises?.length ?? 0) > 1 && !isAdmin();

  // Clear any stores selected under a previous franchise so a franchise switch can't smuggle
  // stale cross-franchise stores into the request.
  useEffect(() => {
    if (isMultipleFranchiseUser) {
      setSelectedStores([]);
      setCheckedAllStores(false);
    }
  }, [isMultipleFranchiseUser, selectedFranchise?.franchiseId]);
  const getFilteredStores = () => {
    if (isAdmin()) {
      return user?.stores ?? [];
    }
    return (user?.stores ?? []).filter(store => store.franchiseId === selectedFranchise?.franchiseId);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setIsGenerating(true);
    setErrorMessage('');
    setSuccessMessage('');

    const reportDate = new Date(new Date(date).setHours(0, 0, 0, 0));
    const filteredStores = getFilteredStores();
    const selectedAllStores = !isMultipleFranchiseUser && (checkedAllStores || (selectedStores.length > 0 && selectedStores.length === filteredStores.length));
    let storesOnRequest = selectedStores.map(x => x.storeNumber as string);

    // If user has multiple franchises and AllStore is checked, restrict to current franchise stores and set AllStore to false
    if (isMultipleFranchiseUser && checkedAllStores) {
      storesOnRequest = filteredStores.map(x => x.storeNumber as string);
    }

    const request: StockCountSummaryReportRequest = {
      reportOnAllAvailableStores: selectedAllStores,
      stores: selectedAllStores ? undefined : storesOnRequest,
      date: formatDateForReport(reportDate),
    };

    runStockCountSummaryReport(request).then(() => {
      setSuccessMessage(t('reporting.generic.post-generation-message'));
      setIsRequestSubmitted(true);
    }).catch((error) => {
      if (typeof(error) === 'string') {
        setErrorMessage(error);
      }
      console.log(error);
    }).finally(() => {
      setIsGenerating(false);
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container
        spacing={10}
        sx={{ pt: '10px' }}>
        <Grid item
          sm={6}
          xs={12}>
          <FormControl fullWidth>
            <Autocomplete
              fullWidth
              multiple
              limitTags={2}
              options={getFilteredStores()}
              value={selectedStores}
              onChange={handleSelectChange}
              disabled={checkedAllStores}
              getOptionKey={(option) => option.storeNumber ?? ''}
              getOptionLabel={(option) => option.storeName ?? ''}
              renderInput={(params) => (
                <TextField {...params}
                  required={selectedStores.length === 0 && !checkedAllStores}
                  label={t('reporting.select-stores')}
                  placeholder={t('reporting.select-stores')} />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <FormGroup style={{ textAlign: 'right' }}>
            <FormControlLabel
              control={<Checkbox
                checked={checkedAllStores}
                onChange={handleSelectAllChange} />}
              label={t('reporting.all-stores')}
            />
          </FormGroup>
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <TextField
            label={t('date')}
            type="date"
            required
            value={date}
            onChange={handleDateChange}
            InputLabelProps={{ shrink: true }}
            InputProps={{ inputProps: { min: getMinDate(), max: getMaxDate() } }}
            fullWidth
          />
        </Grid>
        {
          errorMessage && 
          <Grid item
            xs={12}>
            <Alert severity="error">{errorMessage}</Alert>
          </Grid>
        }
        {
          successMessage && 
          <Grid item
            xs={12}>
            <Alert severity="success">{successMessage}</Alert>
          </Grid>
        }
        <Grid item
          xs={12}
          sx={{ display: 'flex', justifyContent: 'right', columnGap: '10px' }}>
          <Button size='lg'
            variant='secondary' 
            onClick={props.onClose}>{t('cancel')}</Button>
          <Button size='lg'
            disabled={isGenerating || isRequestSubmitted}
            type='submit'>{t('reporting.generate')}</Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default StockCountSummaryReportForm;